import React from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const PRESETS = {
  product: {
    sellable: true,
    purchasable: true,
    stockTracked: true,
    reservable: false,
    shippable: true,
    publicVisible: true,
    expenseEnabled: false,
    internalConsumptionEnabled: false,
  },
  service: {
    sellable: true,
    purchasable: false,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: true,
    expenseEnabled: false,
    internalConsumptionEnabled: false,
  },
  supply: {
    sellable: false,
    purchasable: true,
    stockTracked: true,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: true,
  },
  expense: {
    sellable: false,
    purchasable: true,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: false,
  },
  asset: {
    sellable: false,
    purchasable: true,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: false,
  },
  charge: {
    sellable: true,
    purchasable: true,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: false,
  },
};

export function capabilitiesForKind(kind) {
  return { ...(PRESETS[kind] || PRESETS.product) };
}

function CapabilitySwitch({ label, detail, checked, onChange, compact = false }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: "rgba(25,36,47,0.035)",
        border: "1px solid rgba(25,36,47,0.07)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
        <Box>
          <Typography sx={{ fontWeight: 850, color: BRAND.dark }}>{label}</Typography>
          {!compact ? (
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.58)" }}>
              {detail}
            </Typography>
          ) : null}
        </Box>
        <Switch checked={checked} onChange={(event) => onChange(event.target.checked)} />
      </Stack>
    </Box>
  );
}

function AccountSelect({ label, value, onChange, accounts, allowedTypes }) {
  const available = (accounts || []).filter(
    (account) => account.active !== false && (!allowedTypes?.length || allowedTypes.includes(account.type)),
  );

  return (
    <TextField select label={label} value={value || ""} onChange={onChange} fullWidth>
      {available.map((account) => (
        <MenuItem key={account.code} value={account.code}>
          {account.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

function formatStockValue(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "0";

  return numericValue.toLocaleString("es-AR", {
    maximumFractionDigits: 3,
  });
}

export default function ItemOperationalFields({
  form,
  setForm,
  canManageCosts = false,
  canManageAccounting = false,
  stockManagedBySystem = false,
  accounts = [],
  taxProfiles = [],
  isExisting = false,
  canRegisterPurchase = false,
  canAdjustInventory = false,
  onCreateMovement,
  compact = false,
}) {
  const change = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const changeCapability = (field, value) =>
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "reservable" ? { requiresBooking: value } : {}),
      ...(field === "shippable" ? { requiresShipping: value } : {}),
    }));
  const activeTaxProfiles = (taxProfiles || []).filter((profile) => profile.active !== false);
  const selectedTax = activeTaxProfiles.find((profile) => profile.code === form.taxProfileCode);
  const itemKind = String(form.erpItemKind || form.itemType || "product").toLowerCase();
  const supportsInventory = itemKind === "product" || itemKind === "supply";
  const supportsShipping = itemKind === "product";
  const tracksInventory = supportsInventory && form.stockTracked === true;
  const stockReadOnly = stockManagedBySystem || Boolean(form.hasVariants);
  const hasUsefulCost = Number(form.averageCost || form.lastCost || form.standardCost || 0) > 0;

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 3 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
              {compact ? "Dónde aparece" : "Cómo participa en la operación"}
            </Typography>
            {!compact ? (
              <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.6)" }}>
                Activá solamente los usos reales. Esto define dónde aparece el ítem y qué ocurre al venderlo, comprarlo o consumirlo.
              </Typography>
            ) : null}
          </Box>
          <Divider />
          <Grid container spacing={1.3}>
            <Grid item xs={12} md={6}>
              <CapabilitySwitch
                compact={compact}
                label="Se puede vender"
                detail="Aparece en ventas e ingresos de caja."
                checked={Boolean(form.sellable)}
                onChange={(value) => change("sellable", value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CapabilitySwitch
                compact={compact}
                label="Se puede comprar"
                detail="Aparece en compras y egresos de caja."
                checked={Boolean(form.purchasable)}
                onChange={(value) => change("purchasable", value)}
              />
            </Grid>
            {supportsInventory ? (
              <Grid item xs={12} md={6}>
                <CapabilitySwitch
                  compact={compact}
                  label="Controla inventario"
                  detail="Las ventas descuentan y las compras suman existencias."
                  checked={Boolean(form.stockTracked)}
                  onChange={(value) => change("stockTracked", value)}
                />
              </Grid>
            ) : null}
            {supportsShipping ? (
              <Grid item xs={12} md={6}>
                <CapabilitySwitch
                  compact={compact}
                  label="Admite envío o entrega"
                  detail="El cliente puede recibir este producto mediante una entrega física."
                  checked={Boolean(form.shippable ?? form.requiresShipping)}
                  onChange={(value) => changeCapability("shippable", value)}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} md={6}>
              <CapabilitySwitch
                compact={compact}
                label="Visible en la página"
                detail="Puede mostrarse en el catálogo público."
                checked={Boolean(form.publicVisible)}
                onChange={(value) => change("publicVisible", value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CapabilitySwitch
                compact={compact}
                label="Se puede registrar como gasto"
                detail="Aparece como concepto de gasto pagado."
                checked={Boolean(form.expenseEnabled)}
                onChange={(value) => change("expenseEnabled", value)}
              />
            </Grid>
            {tracksInventory ? (
              <Grid item xs={12} md={6}>
                <CapabilitySwitch
                  compact={compact}
                  label="Permite consumo interno"
                  detail="Descuenta inventario sin registrarlo como venta."
                  checked={Boolean(form.internalConsumptionEnabled)}
                  onChange={(value) => change("internalConsumptionEnabled", value)}
                />
              </Grid>
            ) : null}
          </Grid>
        </Stack>
      </Paper>

      {tracksInventory ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                Inventario
              </Typography>
              {!compact ? (
                <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.6)" }}>
                  {stockManagedBySystem
                    ? "La existencia se muestra sólo como lectura. Para sumar, descontar o corregir unidades usá movimientos confirmados del Sistema."
                    : "Sin el módulo Sistema, podés mantener una existencia manual para el catálogo."}
                </Typography>
              ) : null}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                {stockReadOnly ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      minHeight: 112,
                      px: 2,
                      py: 1.5,
                      borderRadius: 2.5,
                      borderColor: stockManagedBySystem
                        ? "rgba(243,90,58,0.28)"
                        : "rgba(25,36,47,0.12)",
                      bgcolor: stockManagedBySystem
                        ? "rgba(243,90,58,0.045)"
                        : "rgba(25,36,47,0.025)",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.62)", fontWeight: 800 }}>
                      {form.hasVariants ? "Stock total de variantes" : "Existencia actual"}
                    </Typography>
                    <Typography
                      component="output"
                      aria-label={form.hasVariants ? "Stock total de variantes" : "Existencia actual"}
                      sx={{ color: BRAND.dark, fontSize: "1.65rem", lineHeight: 1.15, fontWeight: 950 }}
                    >
                      {formatStockValue(form.stock)}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 0.5, color: "rgba(25,36,47,0.56)" }}>
                      {form.hasVariants
                        ? "Se calcula sumando las variantes activas."
                        : "Se actualiza únicamente desde movimientos confirmados."}
                    </Typography>
                  </Paper>
                ) : (
                  <TextField
                    type="number"
                    label="Stock manual"
                    value={form.stock}
                    onChange={(event) => change("stock", event.target.value)}
                    inputProps={{
                      min: form.allowNegativeStock ? undefined : 0,
                      step: "any",
                    }}
                    helperText="Valor simple del catálogo; no genera movimientos ni asientos contables."
                    fullWidth
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  label="Mínimo deseado"
                  value={form.minimumStock}
                  onChange={(event) => change("minimumStock", event.target.value)}
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  label="Avisar al llegar a"
                  value={form.reorderPoint}
                  onChange={(event) => change("reorderPoint", event.target.value)}
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Ubicación"
                  value={form.inventoryLocation}
                  onChange={(event) => change("inventoryLocation", event.target.value)}
                  placeholder="Depósito, estante o sector"
                  fullWidth
                />
              </Grid>
            </Grid>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ sm: "center" }}>
              {stockManagedBySystem ? (
                isExisting ? (
                  <>
                    {form.purchasable && canRegisterPurchase ? (
                      <Button
                        variant="contained"
                        onClick={() => onCreateMovement?.("purchase")}
                        sx={{ textTransform: "none", fontWeight: 850 }}
                      >
                        Registrar compra en movimientos
                      </Button>
                    ) : null}
                    {canAdjustInventory ? (
                      <Button
                        variant="outlined"
                        onClick={() => onCreateMovement?.("stock_adjustment")}
                        sx={{ textTransform: "none", fontWeight: 850 }}
                      >
                        Ajustar desde movimientos
                      </Button>
                    ) : null}
                    {(form.purchasable && canRegisterPurchase) || canAdjustInventory ? (
                      <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.58)" }}>
                        La compra suma unidades y registra su origen. El ajuste corrige diferencias y conserva la auditoría.
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.58)" }}>
                        Tu rol puede consultar la existencia, pero no registrar compras ni ajustes.
                      </Typography>
                    )}
                  </>
                ) : (
                  <Alert severity="info" sx={{ width: "100%" }}>
                    El ítem se guardará con existencia cero. Guardalo primero y después registrá el inventario inicial, una compra o un ajuste desde Movimientos.
                  </Alert>
                )
              ) : (
                <Alert severity="info" sx={{ width: "100%" }}>
                  Este es un stock manual de catálogo. Al activar el Sistema, quedará bloqueado y los cambios deberán registrarse como movimientos.
                </Alert>
              )}
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.allowNegativeStock)}
                  onChange={(event) => change("allowNegativeStock", event.target.checked)}
                />
              }
              label="Permitir ventas sin existencia suficiente"
            />
          </Stack>
        </Paper>
      ) : null}

      {canManageCosts && (form.purchasable || tracksInventory || form.expenseEnabled) ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                Costo interno
              </Typography>
              {!compact ? (
                <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.6)" }}>
                  Las compras confirmadas actualizan el último costo y el promedio. El costo de referencia sirve como valor inicial o estimado y no se muestra al público.
                </Typography>
              ) : null}
            </Box>
            {!hasUsefulCost && form.sellable ? (
              <Alert severity="warning">
                Cargá al menos un costo para que las ventas reflejen un margen real.
              </Alert>
            ) : null}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  label="Último costo"
                  value={form.lastCost}
                  InputProps={{ readOnly: true }}
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  label="Costo promedio"
                  value={form.averageCost}
                  InputProps={{ readOnly: true }}
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  label="Costo de referencia"
                  value={form.standardCost}
                  onChange={(event) => change("standardCost", event.target.value)}
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Costo que debe usar el sistema"
                  value={form.costingMethod}
                  onChange={(event) => change("costingMethod", event.target.value)}
                  fullWidth
                >
                  <MenuItem value="last_cost">Último costo</MenuItem>
                  <MenuItem value="average_cost">Costo promedio</MenuItem>
                  <MenuItem value="standard_cost">Costo de referencia</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      {canManageAccounting ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                Cuentas e impuestos
              </Typography>
              {!compact ? (
                <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.6)" }}>
                  Estas decisiones se copian en cada operación para conservar el historial aunque después cambie el ítem.
                </Typography>
              ) : null}
            </Box>

            {!accounts.length ? (
              <Alert severity="warning">
                No se pudieron cargar las cuentas. Actualizá la estructura del sistema antes de guardar.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {form.sellable ? (
                  <Grid item xs={12} md={6}>
                    <AccountSelect
                      label="Dónde registrar las ventas"
                      value={form.salesAccountCode}
                      onChange={(event) => change("salesAccountCode", event.target.value)}
                      accounts={accounts}
                      allowedTypes={["income"]}
                    />
                  </Grid>
                ) : null}
                {form.purchasable ? (
                  <Grid item xs={12} md={6}>
                    <AccountSelect
                      label="Dónde registrar las compras"
                      value={form.purchaseAccountCode}
                      onChange={(event) => change("purchaseAccountCode", event.target.value)}
                      accounts={accounts}
                      allowedTypes={["expense", "asset"]}
                    />
                  </Grid>
                ) : null}
                {tracksInventory ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <AccountSelect
                        label="Valor del inventario"
                        value={form.inventoryAccountCode}
                        onChange={(event) => change("inventoryAccountCode", event.target.value)}
                        accounts={accounts}
                        allowedTypes={["asset"]}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <AccountSelect
                        label="Costo de lo vendido"
                        value={form.costAccountCode}
                        onChange={(event) => change("costAccountCode", event.target.value)}
                        accounts={accounts}
                        allowedTypes={["expense"]}
                      />
                    </Grid>
                  </>
                ) : null}
                {form.expenseEnabled || form.internalConsumptionEnabled ? (
                  <Grid item xs={12} md={6}>
                    <AccountSelect
                      label="Dónde registrar el gasto"
                      value={form.expenseAccountCode}
                      onChange={(event) => change("expenseAccountCode", event.target.value)}
                      accounts={accounts}
                      allowedTypes={["expense"]}
                    />
                  </Grid>
                ) : null}
                {(form.sellable || form.purchasable || form.expenseEnabled) ? (
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Impuesto aplicable"
                      value={form.taxProfileCode || ""}
                      onChange={(event) => change("taxProfileCode", event.target.value)}
                      fullWidth
                    >
                      <MenuItem value="">Sin impuesto configurado</MenuItem>
                      {activeTaxProfiles.map((profile) => (
                        <MenuItem key={profile.code} value={profile.code}>
                          {profile.name}
                          {Number(profile.rate || 0)
                            ? profile.calculationMode === "fixed"
                              ? ` · importe fijo ${Number(profile.rate)}`
                              : ` · ${Number(profile.rate)}%`
                            : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                ) : null}
              </Grid>
            )}

            {form.sellable ? (
              <Alert severity={selectedTax ? "success" : "warning"}>
                {selectedTax
                  ? `El ítem está preparado para calcular impuestos con ${selectedTax.name || "la configuración elegida"}. Las operaciones conservarán ese cálculo.`
                  : "El ítem todavía no está preparado para facturar: las ventas se registrarán sin impuesto."}
                {" "}La emisión de un comprobante fiscal requiere además un proveedor de facturación conectado al proyecto.
              </Alert>
            ) : null}
          </Stack>
        </Paper>
      ) : compact ? null : (
        <Alert severity="info">
          Los costos, las cuentas y los impuestos aparecen únicamente para personas autorizadas.
        </Alert>
      )}
    </Stack>
  );
}
